import * as React from 'react';

import { ApplicationState, ConnectedReduxProps } from '../../store';
import { CaseDocumentsRequest, createDocumentRequest, createCaseRequest } from '../../store/details/shared/model';
import Dropzone, { DropEvent, FileRejection } from 'react-dropzone';
import { replaceImages, uploadImages } from '../../store/details/images/actions';
import { BelfiusButton } from '../../components/custom/belfius-button';
import BelfiusButtonWrapper from '../../components/custom/belfius-button-wrapper';
import { CaseResponse } from '../../store/case/model';
import { CommunicationOverviewDocumentItem } from '../../store/details/overview/types';
import { Dispatch } from 'redux';
import { Image, createImage } from '../../store/details/images/model';
import { ImagesUIState } from '../../store/details/images/types';
import { PolicySubTypeTCEnum } from '../../store/details/policy/types';
import { connect } from 'react-redux';
import { environment } from '../../environment/environment';
import { fetchCaseForMobile } from '../../store/case';
import { getI18n } from '../../store/utils/i18n/strings.manager';
import styles from './MobileImages.module.css';
import { toggleIsUIDisabled } from '../../store/shared/actions';

const Jimp = require('jimp');

type FileWithPreview = File & {
  preview: string;
};

interface PropsFromState {
  readonly currentClaimNotificationId: number;
  readonly images: Image[];
  readonly sentDocuments: CommunicationOverviewDocumentItem[];
  readonly selectedCase: CaseResponse;
  readonly currentLanguage: string;
  readonly isUIDisabled: boolean;
  readonly showImageUploadedMessage: boolean;
  readonly caseCode: string;
  readonly instructionId: number;
}

interface PropsFromDispatch {
  readonly fetchCaseForMobile: typeof fetchCaseForMobile;
  readonly uploadImages: typeof uploadImages;
  readonly replaceImages: typeof replaceImages;
  readonly toggleIsUIDisabled: typeof toggleIsUIDisabled;
}

type AllProps = PropsFromState & PropsFromDispatch & ConnectedReduxProps;
class MobileImages extends React.Component<AllProps> {
  private dropzoneRef: any;
  private translations = getI18n();
  constructor(props: AllProps) {
    super(props);
    this.translations = getI18n();
    this.translations.setLanguage(this.props.currentLanguage);
  }

  componentDidMount() {
    this.props.fetchCaseForMobile(this.props.caseCode);
  }

  getInsuranceLogoIcon() {
     switch (this.props.selectedCase.policySubTypeTC) {
       case PolicySubTypeTCEnum.BELFIUS_HOME:
         return <img src={require('../../assets/images/belfius_logo_218x46.png')} className={styles.policyTypeIcon} alt="" />;
       case PolicySubTypeTCEnum.DVV_BRAND:
         if (this.props.currentLanguage === 'nl-BE') {
           return <img src={require('../../assets/images/dvv_nl.png')} className={styles.policyTypeIcon} alt="" />;
         } else {
           return <img src={require('../../assets/images/dvv_fr.png')} className={styles.policyTypeIcon} alt="" />;
         }
       default:
         return '';
     }
   }

  async onFilesDropped(files: FileWithPreview[]) {
    this.props.toggleIsUIDisabled(true);
    for (let i = 0; i < files.length; i++) {
      if (!((files[i].name.indexOf('.jpeg') > -1 ||
      files[i].name.indexOf('.jpg') ||
      files[i].name.indexOf('.png')) &&
      files[i].type !== 'application/x-msdownload')) {
        return;
      }
    }
    const imagePromises = files.map(x => {
      return new Promise<Image>((resolve) => {
        const image = createImage();
        image.filename = x.name;
        image.url = x.preview || '';
        image.isChecked = false;

        const fileReader = new FileReader();
        fileReader.onloadend = () => {
          let c = '';
          if (typeof fileReader.result === 'string') {
            c = fileReader.result.split(',')[1] || '';
          } else if (fileReader.result instanceof ArrayBuffer) {
            const uint8Array = new Uint8Array(fileReader.result);
            const binaryString = String.fromCharCode(...uint8Array);
            c = binaryString.split(',')[1] || '';
          }
          const fc = atob(c);
          const fcf = fc.charAt(0) + fc.charAt(1);
          if (fcf !== 'MZ') {
            const buffer = Buffer.from(c , 'base64');
            Promise.resolve(this.resizeImage(buffer))
            .then((value) => {
              image.content = value.split(',')[1];
              resolve(image);
              return;
            });
            return;
          } else {
            return;
          }
        };
        fileReader.readAsDataURL(x);
        return;
      });
    });
    const droppedImages = await Promise.all(imagePromises);
    const images = [...this.props.images, ...droppedImages];
    this.props.replaceImages(images);
    this.uploadImages();
  }

  async resizeImage (buffer: Buffer ) {
    const jimpImage = await Jimp.read(buffer);
    let resizedImage = jimpImage.quality(environment.imageQuality);
    const originalHeight = resizedImage.getHeight();
    const originalWidth = resizedImage.getWidth();
    if (originalHeight > environment.maxImageHeight || originalWidth > environment.maxImageWidth) {
      resizedImage = originalHeight * environment.maxImageWidth  > originalWidth * environment.maxImageHeight
        ? await jimpImage.resize(Jimp.AUTO, environment.maxImageHeight)
        : await jimpImage.resize(environment.maxImageWidth, Jimp.AUTO);
    }
    const resizedImagePromise = new Promise<string>((resolve) => {
          const result = resizedImage.getBase64Async(resizedImage.getMIME());
          resolve(result);
          return;
      });
    return await Promise.resolve(resizedImagePromise);
  }

  private uploadImages() {
    const imageRequests = this.props.images
      .filter(x => !x.isOnline)
      .map(x => {
        const imageRequest = createDocumentRequest();
        imageRequest.filename = x.filename;
        imageRequest.description = x.description;
        imageRequest.content = x.content;
        return imageRequest;
      });
    const request = createCaseRequest();
    request.claimNotificationId = this.props.currentClaimNotificationId;
    request.documents = imageRequests;
    this.props.uploadImages(this.props.instructionId, request);
  }

  renderAddressData() {
    const riskCity = this.props.selectedCase.riskCity;
    const riskAddressData = `${this.props.selectedCase.riskStreet} ${this.props.selectedCase.riskBuildingNumber} ${this.props.selectedCase.riskBoxNumber !== null && this.props.selectedCase.riskBoxNumber !== '?' ? this.props.selectedCase.riskBoxNumber : ``}`;
    const insuredCity = this.props.selectedCase.policyHolderCity;
    const insuredAddressData = `${this.props.selectedCase.policyHolderStreet} ${this.props.selectedCase.policyHolderBuildingNumber} ${this.props.selectedCase.policyHolderBoxNumber !== null && this.props.selectedCase.riskBoxNumber !== '?' ? this.props.selectedCase.riskBoxNumber : ``}`;
    const phone = this.props.selectedCase.telephone !== `null` &&  this.props.selectedCase.telephone !== `?` ? this.props.selectedCase.telephone.replace(' ', '/').replace(/\s/g, '') : '';
    if (riskAddressData === insuredAddressData || this.props.selectedCase.addressId === null) {
      return (
        <div>
          <div className={styles.city}>{riskCity}</div>
          <div className={styles.address}>{riskAddressData}</div>
          {phone !== '' ? <div className={styles.phone}>{phone}</div> : ''}
        </div>
      );
    } else {
      return (
        <div>
          <div className={styles.city}>{riskCity}</div>
          <div className={styles.address}>{riskAddressData}</div>
          {phone !== '' ? <div>{phone}</div> : ''}
          <div className={styles.city}>{insuredCity}</div>
          <div className={styles.address}>{insuredAddressData}</div>
          {phone !== '' ? <div className={styles.phone}>{phone}</div> : ''}
        </div>
      );
    }
  }

  public render() {
    const handleDrop = (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
      const filesWithPreview = acceptedFiles.map(file =>
          Object.assign(file, {
              preview: URL.createObjectURL(file)
          })
      ) as FileWithPreview[];
      this.onFilesDropped(filesWithPreview);
    };

    return (
      <div className={styles.container}>
        <div className={styles.images}>
        <Dropzone
          ref={(node: any) => { this.dropzoneRef = node; }}
          accept={{
            'image/*': []
          }}
          onDrop={handleDrop}
          noClick={true} />
        </div>
          <div className={styles.content}>
            <div className={styles.caseContainer}>
            <div className={styles.damageType}>
              <div>
                {this.props.selectedCase && this.props.selectedCase.damageIcon ? <img src={require('../../assets/images/' + this.props.selectedCase.damageIcon + '_75x75.png')} className={styles.damageTypeIcon} alt=""/> : ''}
              </div>
              <div className={styles.insuranceLogo}>
                {this.props.selectedCase ? this.getInsuranceLogoIcon() : ''}
              </div>
            </div>
            <div className={styles.caseCode}>{this.props.selectedCase.caseCode}</div>
            <div className={styles.insuredPersonName}>{this.props.selectedCase.insuredPersonName}</div>
            <div>{this.renderAddressData()}</div>
            <div id='loader' className={styles.searchcontainer}>
            <div className={styles.loader}>
              <img hidden={!this.props.isUIDisabled} src={require('../../assets/images/spinnerLoaderGray.gif')} alt="" />
            </div>
          </div>
          </div>
            <div className={styles.formContainer}>
              <BelfiusButton
                className={styles.button}
                onClick={() => this.dropzoneRef.open()}
                title={this.translations.get('images.pictures.button')} />
              <BelfiusButtonWrapper
                className={styles.button}
                navigateTo='/'
                title={this.translations.get('images.back.button')} />
              <span hidden={!this.props.showImageUploadedMessage} className={styles.successMessage}>{this.translations.get('images.imageUploadedMessage')}</ span>
            </div>
          </div>
      </div>
    );
  }
}

const mapStateToProps = ({ shared, caseState, details: { detailsImages, detailsOverview } }: ApplicationState): PropsFromState => ({
  currentClaimNotificationId: caseState.selectedCase.claimNotificationId,
  images: detailsImages.images,
  sentDocuments: detailsOverview.documentList,
  selectedCase: caseState.selectedCase,
  currentLanguage: shared.currentLanguage,
  isUIDisabled: shared.isUIDisabled,
  showImageUploadedMessage: detailsImages.requestState === ImagesUIState.Uploaded,
  caseCode: caseState.selectedCase.caseCode,
  instructionId: caseState.selectedCase.instructionId,
});

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => ({
  fetchCaseForMobile: (caseCode: string) => dispatch(fetchCaseForMobile(caseCode)),
  uploadImages: (instructionId: number, request: CaseDocumentsRequest) => dispatch(uploadImages(instructionId, request)),
  replaceImages: (images: Image[]) => dispatch(replaceImages(images)),
  toggleIsUIDisabled: (disabled: boolean) => dispatch(toggleIsUIDisabled(disabled))
});

export default connect(mapStateToProps, mapDispatchToProps)(MobileImages);
